import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

// =============================================================================
// SINGLE SOURCE OF TRUTH FOR SUPABASE CONFIG
//
// To switch environments, edit .env.local:
//   - Uncomment the VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY for your target env
//   - Comment out all other VITE_SUPABASE_* lines
//   - Restart the dev server (npm run dev)
//
// If no VITE_SUPABASE_URL is set, defaults to PRODUCTION
// =============================================================================

// Environment configs (for reference - actual values come from .env.local or defaults)
const CONFIGS = {
  local: {
    url: "http://127.0.0.1:54321",
    publishableKey: null,
    anonKey: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZS1kZW1vIiwicm9sZSI6ImFub24iLCJleHAiOjE5ODM4MTI5OTZ9.CRXP1A7WOeoJeXxjNni43kdQwgnWNReilDMblYTn_I0",
  },
  staging: {
    url: "https://zsldtfccocajktuivlqh.supabase.co",
    publishableKey: null,
    anonKey: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InpzbGR0ZmNjb2Nhamt0dWl2bHFoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTU2OTk4NzksImV4cCI6MjA3MTI3NTg3OX0.crCKJo8ZVIWV5JFW9ejTX6hfjjzOXKI72iCw_q4KWQo",
  },
  production: {
    url: "https://myeyoafugkrkwcnfedlu.supabase.co",
    publishableKey: "sb_publishable_wBXAmimkZ2-GzK0uPuI6Ow_bVoZe_ca",
    anonKey: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im15ZXlvYWZ1Z2tya3djbmZlZGx1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDMxMTEzMTAsImV4cCI6MjA1ODY4NzMxMH0.aYCbR62ym2XYDdY6Ss6sGj14yOy3i8wj9f5gHujmqDI",
  },
};

// Get environment from .env.local or default to production
const env = (import.meta.env.VITE_ENVIRONMENT as keyof typeof CONFIGS) || 'production';
const config = CONFIGS[env] || CONFIGS.production;

// Allow override from VITE_SUPABASE_URL if set
export const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || config.url;
const resolvedKey =
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
  config.publishableKey ||
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  config.anonKey;

if (!resolvedKey) {
  throw new Error(`[Supabase] No API key configured for environment: ${env}`);
}

export const SUPABASE_PUBLISHABLE_KEY = resolvedKey;
export const SUPABASE_ANON_KEY = SUPABASE_PUBLISHABLE_KEY;

// For storage URLs (images, etc.)
export const SUPABASE_STORAGE_URL = `${SUPABASE_URL}/storage/v1/object/public`;

// Log which environment is active (only in dev)
if (import.meta.env.DEV) {
  const keyType = SUPABASE_PUBLISHABLE_KEY.startsWith('sb_publishable_') ? 'publishable' : 'legacy-jwt';
  console.log(`[Supabase] Environment: ${env}, URL: ${SUPABASE_URL}, Key type: ${keyType}`);
}

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY);

// This is a properly typed version of the client that includes the 'from' method
// Use this when you need to access methods like 'from', 'auth', etc.
export const supabaseClient = supabase as any;
